import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import SeoHead from "../components/SeoHead";
import { api } from "../lib/api";

/**
 * /categories — Index of all curated ingredient categories.
 *
 * Lightweight grid linking to each /categories/:slug detail page.
 * Mirrors the homepage CategoryShowcase styling but in a dedicated SEO
 * page so Google can crawl every category as a first-class landing.
 */
export default function CategoryIndex() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/seo/categories")
      .then(({ data }) => { if (!cancelled) setItems(data?.items || []); })
      .catch(() => { /* leave empty */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <main
      data-testid="category-index-page"
      className="bg-cream-50 min-h-[80vh]"
    >
      <SeoHead
        title="Skincare ingredient categories — BlendSafe"
        description="Browse skincare ingredient categories (retinoids, exfoliants, vitamin C, niacinamide, SPF and more) and the medications they interact with."
        canonicalPath="/categories"
      />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-[10.5px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
          Ingredient categories
        </p>
        <h1
          data-testid="category-index-headline"
          className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.025em] text-ink mt-2.5 leading-[1.08]"
        >
          Browse skincare by category.
        </h1>
        <p className="text-[14.5px] sm:text-[15.5px] text-ink-soft/85 mt-5 leading-relaxed max-w-xl">
          Each category collects the ingredients you'll see on a label and
          explains how they interact with common medications.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-ink-mute text-[13px]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading categories…
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 gap-3.5">
            {items.map((c) => (
              <Link
                key={c.slug}
                to={`/categories/${c.slug}`}
                data-testid={`category-index-card-${c.slug}`}
                className="group rounded-2xl bg-white border border-surface-line p-5 hover:border-ink-mute transition-colors"
              >
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">
                  Category
                </p>
                <h2 className="mt-1 text-[18px] font-semibold text-ink">{c.label}</h2>
                <p className="mt-2 text-[13.5px] text-ink-soft leading-relaxed line-clamp-3">
                  {c.tagline}
                </p>
                {c.use_cases?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.use_cases.map((u) => (
                      <span
                        key={u}
                        className="inline-flex items-center rounded-full bg-cream-100 border border-surface-line px-2 py-0.5 text-[10.5px] text-ink-soft"
                      >
                        {u}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-semibold text-ink group-hover:gap-2 transition-all">
                  Explore <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
